import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, CircularProgress, Stack, Typography } from '@mui/material';
import { userManager } from './AuthContext';

/**
 * OIDC redirect target. Keycloak sends the browser back here after login;
 * we finalize the code exchange, then bounce to wherever the user was
 * going (or `/` if unknown).
 *
 * Never rendered as part of AppShell — it's a bare page that finishes the
 * auth handshake and immediately navigates away.
 */
export function CallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    userManager
      .signinRedirectCallback()
      .then((user) => {
        const from = (user.state as { from?: string } | undefined)?.from ?? '/';
        navigate(from, { replace: true });
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [navigate]);

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{ minHeight: '100vh', p: 4 }}
    >
      {error ? (
        <Alert severity="error" sx={{ maxWidth: 480 }}>
          Sign-in failed: {error}
        </Alert>
      ) : (
        <>
          <CircularProgress />
          <Typography color="text.secondary">Finishing sign-in…</Typography>
        </>
      )}
    </Stack>
  );
}
